import React, { useState, useMemo } from "react";
import ProductItem from "../../../../widgets/ProductItem";
import {
  Autocomplete,
  Box,
  Card,
  Grid,
  Pagination,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import CustomCircularP from "../../../../widgets/CustomCircularP";
import FilterDialogMui from "../../../../widgets/FilterDialogMui";
import AlphabetSortWidget from "./AlphabetSortWidget";
import useProductCategories from "../../visitor/registration/hooks/useProductCategories";
import useApiEp from "../../../../hooks/useApiEp";

const Products = () => {
  // --- States ---
  const [page, setPage] = useState(1);
  const [sq, setSq] = useState("");
  const [openFilter, setOpenFilter] = useState(false);

  // Dialog Filters
  const [selectedHall, setSelectedHall] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");

  // Category Filter (Multiselect)
  const [selectedCategories, setSelectedCategories] = useState([]);

  // --- API Fetch ---
  const { data: Mdata, isLoading: loadingCategories } = useProductCategories();
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-explore-products?page=${page}&sq=${sq}&hall=${selectedHall}&country=${selectedCountry}&letter=${selectedLetter}&cat=${selectedCategories.join(
      ","
    )}`
  );

  // --- Dynamic Category Logic ---
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    // Determine which property to read based on Mdata.type
    const type = Mdata.type || "single";
    let key = "MainCategory";
    if (type === "double") key = "Category";
    if (type === "triple") key = "SubCategory";

    return [
      ...new Set(Mdata.categories.map((item) => item[key]).filter(Boolean)),
    ].sort();
  }, [Mdata]);

  // --- Handlers ---
  const onItemSearch = (e) => {
    setSq(e.target.value);
    setPage(1);
  };

  const handleCategoriesChange = (event, newValue) => {
    setSelectedCategories(newValue);
    setPage(1);
  };

  const handleLetterSelect = (newV) => {
    setSelectedLetter(newV || "");
    setPage(1);
  };

  const onPageChange = (e, newPage) => {
    setPage(newPage);
  };

  // --- Product Handlers ---
  const onEditProduct = () => {};
  const onDeleteProduct = () => {};
  const onSelectProduct = (product) => {};

  // --- Helpers ---
  const totalPages = data?.totalItems ? Math.ceil(data.totalItems / 30) : 0;
  const hasProducts = data?.data && data.data.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <CustomCircularP show={isLoading || loadingCategories} />

      <FilterDialogMui
        selectedCountry={selectedCountry}
        setSelectedCountry={(val) => {
          setSelectedCountry(val);
          setPage(1);
        }}
        selectedHall={selectedHall}
        setSelectedHall={(val) => {
          setSelectedHall(val);
          setPage(1);
        }}
        selectedLetter={selectedLetter}
        setSelectedLetter={(val) => {
          setSelectedLetter(val);
          setPage(1);
        }}
        open={openFilter}
        setOpen={setOpenFilter}
      />

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" fontWeight={800} color="#1e293b">
            Products List
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eef2f6",
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            p: 3,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search Products by name or keyword..."
                variant="outlined"
                value={sq}
                onChange={onItemSearch}
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288d1" },
                  },
                }}
              />
            </Grid>

            {/* Dynamic Categories (Multiselect) */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                size="medium"
                options={uniqueCategories}
                value={selectedCategories}
                onChange={handleCategoriesChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categories"
                    placeholder="Select..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      color="primary"
                      variant="outlined"
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        mb={3}
      >
        <AlphabetSortWidget onSelectLetter={handleLetterSelect} />
      </Stack>

      {/* Product List Content */}
      {!isLoading && !hasProducts ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="30vh"
        >
          <Typography variant="h6" color="textSecondary">
            No products found matching your criteria.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {data?.data?.map((item, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={item.id || index}
              sx={{ display: "flex" }}
            >
              <ProductItem
                viewMode={true}
                onSelectProduct={onSelectProduct}
                onEditProduct={onEditProduct}
                onDeleteProduct={onDeleteProduct}
                product={item}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box mt={4} mb={2} justifyContent="center" display="flex">
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default Products;
